/**
 * Profile primitives. Deliberately a little bigger and warmer than the Settings
 * ones: Josie and Nick use this screen more than anyone, on a phone, so targets
 * are ≥44px, labels are real labels, and focus is always visible.
 *
 * Token-only (src/styles.css). Nothing here hardcodes a member's color — that
 * comes from D1 and is passed in.
 */
import {
  useId,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
} from 'react';

export const FOCUS =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage focus-visible:ring-offset-2 focus-visible:ring-offset-canvas';

export function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';

const VARIANTS: Record<Variant, string> = {
  primary: 'bg-sage text-white hover:bg-sage/90 disabled:bg-sage/40',
  secondary: 'bg-surface text-ink border border-border hover:bg-surface-sunk',
  ghost: 'text-ink-soft hover:bg-surface-sunk hover:text-ink',
  danger: 'bg-clay-soft text-clay border border-clay/30 hover:bg-clay/15',
};

export function Button({
  variant = 'secondary',
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { variant?: Variant }) {
  return (
    <button
      {...props}
      className={cx(
        'inline-flex min-h-12 items-center justify-center gap-2 rounded-full px-5 text-base font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-60',
        VARIANTS[variant],
        FOCUS,
        className
      )}
    />
  );
}

export function IconButton({
  label,
  className = '',
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <button
      {...props}
      aria-label={label}
      title={label}
      className={cx(
        'inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-border bg-surface text-ink-soft transition-colors hover:bg-surface-sunk hover:text-ink disabled:cursor-not-allowed disabled:opacity-35 disabled:hover:bg-surface',
        FOCUS,
        className
      )}
    />
  );
}

export function Section({
  title,
  hint,
  children,
}: {
  title: string;
  hint?: string;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section
      aria-labelledby={id}
      className="rounded-card border border-border bg-surface p-5 sm:p-6"
    >
      <h2 id={id} className="font-display text-xl font-semibold text-ink">
        {title}
      </h2>
      {hint && <p className="mt-1 text-sm text-ink-soft">{hint}</p>}
      <div className="mt-5">{children}</div>
    </section>
  );
}

interface FieldProps {
  label: string;
  hint?: string;
  error?: string | null;
  children: (props: { id: string; 'aria-describedby': string | undefined }) => ReactNode;
}

/** Label + hint + inline error, wired together with real ids. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-sm font-medium text-ink">
        {label}
      </label>
      {hint && (
        <p id={hintId} className="text-xs text-ink-soft">
          {hint}
        </p>
      )}
      {children({ id, 'aria-describedby': describedBy })}
      {error && (
        <p id={errorId} role="alert" className="text-sm font-medium text-clay">
          <span aria-hidden="true">⚠ </span>
          {error}
        </p>
      )}
    </div>
  );
}

export function TextInput({ className = '', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <input
      {...props}
      className={cx(
        'min-h-12 w-full rounded-xl border border-border bg-surface px-3 text-base text-ink placeholder:text-ink-faint',
        FOCUS,
        className
      )}
    />
  );
}

/**
 * A real checkbox with a big, tappable label around it. Never color alone: the
 * words and the check both say whether it is on.
 */
export function CheckRow({
  checked,
  onChange,
  title,
  subtitle,
  disabled,
}: {
  checked: boolean;
  onChange: (next: boolean) => void;
  title: ReactNode;
  subtitle?: ReactNode;
  disabled?: boolean;
}) {
  return (
    <label
      className={cx(
        'flex min-h-14 cursor-pointer items-center gap-3 rounded-xl px-3 py-2 transition-colors',
        disabled ? 'cursor-not-allowed opacity-60' : 'hover:bg-surface-sunk',
        'focus-within:ring-2 focus-within:ring-sage focus-within:ring-offset-2 focus-within:ring-offset-surface'
      )}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="h-6 w-6 shrink-0 accent-[var(--color-sage)] focus-visible:outline-none"
      />
      <span className="min-w-0 flex-1">
        <span className="block text-base text-ink">{title}</span>
        {subtitle && <span className="block text-xs text-ink-soft">{subtitle}</span>}
      </span>
    </label>
  );
}

/** The alert for a rejected save. Rendered inline, where the person is looking. */
export function AlertNote({ message }: { message: string }) {
  return (
    <p
      role="alert"
      className="rounded-xl border border-clay/30 bg-clay-soft px-4 py-2 text-sm text-clay"
    >
      <span aria-hidden="true">⚠ </span>
      {message}
    </p>
  );
}

/** A failed read. Says what failed and gives a way out — never an all-clear. */
export function ErrorNote({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return (
    <div
      role="alert"
      className="flex flex-col gap-3 rounded-card border border-clay/30 bg-clay-soft px-4 py-3 sm:flex-row sm:items-center sm:justify-between"
    >
      <p className="text-sm text-clay">
        <span aria-hidden="true">⚠ </span>
        {message}
      </p>
      {onRetry && (
        <Button variant="secondary" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  );
}

/** The initial-in-your-color avatar, exactly what the header already draws. */
export function InitialAvatar({
  name,
  color,
  className = '',
}: {
  name: string;
  color: string;
  className?: string;
}) {
  return (
    <span
      aria-hidden="true"
      className={cx(
        'inline-grid place-items-center rounded-full font-semibold text-white select-none',
        className
      )}
      style={{ backgroundColor: color }}
    >
      {(name.trim().slice(0, 1) || '?').toUpperCase()}
    </span>
  );
}

export function Skeleton({ className = '' }: { className?: string }) {
  return <div className={cx('animate-pulse rounded-xl bg-surface-sunk', className)} />;
}
